import { useCallback, useEffect, useState } from 'react';
import {
    detectGcmLocation,
    canUseDeviceLocation,
    getDeviceLocation,
} from '@/services/program/getGcmMerchants';

const STORAGE_KEY = 'gcm_location';

// How the current location was chosen; drives the wording under the search bar
export const LOCATION_SOURCE = {
    DETECTED: 'detected', // IP lookup succeeded
    DEFAULT: 'default',   // lookup failed, showing the fallback city
    MANUAL: 'manual',     // typed by the shopper (or given in the URL)
    DEVICE: 'device',     // browser geolocation, stored as "lat,lng"
};

const readStored = () => {
    try {
        const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
        return stored?.location ? stored : null;
    } catch (_) {
        return null;
    }
};

const store = (value) => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch (_) {}
};

// The shopper's location for the GCM pages, shared across them for the session.
// `preset` (e.g. from the search URL) wins. `location` is null until known, so pages
// fetch once rather than first with a default and again after detection.
const useGcmLocation = (preset) => {
    const [state, setState] = useState(() => {
        const stored = readStored();
        // Links between the GCM pages carry the location; keep its known source
        if (preset && stored?.location !== preset) return { location: preset, source: LOCATION_SOURCE.MANUAL };
        return stored;
    });
    const [locating, setLocating] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (state) return undefined;
        let cancelled = false;
        detectGcmLocation().then(({ location, detected }) => {
            if (cancelled) return;
            const next = { location, source: detected ? LOCATION_SOURCE.DETECTED : LOCATION_SOURCE.DEFAULT };
            setState(next);
            store(next);
        });
        return () => { cancelled = true; };
    }, [state]);

    const update = useCallback((location, source = LOCATION_SOURCE.MANUAL) => {
        const next = { location, source };
        setState(next);
        store(next);
        setError(null);
    }, []);

    const locateDevice = useCallback(async () => {
        setLocating(true);
        setError(null);
        try {
            const coords = await getDeviceLocation();
            update(coords, LOCATION_SOURCE.DEVICE);
            return coords;
        } catch (e) {
            setError(e?.code === 1
                ? 'Location access was blocked in your browser.'
                : "We couldn't get your current location.");
            return null;
        } finally {
            setLocating(false);
        }
    }, [update]);

    return {
        location: state?.location ?? null,
        source: state?.source ?? null,
        setLocation: update,
        locateDevice: canUseDeviceLocation() ? locateDevice : null,
        locating,
        error,
    };
};

export default useGcmLocation;
